import React from 'react';
import { ChevronLeft, ShieldCheck, Database, Cookie, Users, Mail } from 'lucide-react';

const SECTIONS: { Icon: typeof ShieldCheck; title: string; color: string; paragraphs: string[] }[] = [
  {
    Icon: Database,
    title: 'Hvilke opplysninger vi samler inn',
    color: 'var(--blue)',
    paragraphs: [
      'Kontoopplysninger: navn og e-postadresse du oppgir ved registrering.',
      'Gruppedata: hvilke grupper du er medlem av, og hvem som er admin.',
      'Verktøy og inventar: ting du legger inn, bilder du laster opp, utlånshistorikk og lister du oppretter.',
      'Enhetsinformasjon ved 2-faktor autentisering: enhetsnavn og nettlesertype for enheter du velger å huske i 30 dager.',
      'Sted: fylke og kommune du oppgir ved registrering (eller senere i Rediger profil). Brukes utelukkende til å vise deg relevante, lokalt målrettede sponsor-annonser i appen, i stedet for annonser fra bedrifter langt unna deg. Opplysningen deles ikke med sponsorene selv eller andre tredjeparter — den brukes kun internt i appen for å avgjøre hvilke annonser som vises til deg. Du kan se, endre eller fjerne den når som helst under Profil → Rediger.',
    ],
  },
  {
    Icon: Users,
    title: 'Databehandlere og tredjeparter',
    color: 'var(--green)',
    paragraphs: [
      'Supabase (Irland/EU) lagrer alle data i appen — konto, grupper, verktøy og bilder.',
      'Resend brukes til å sende e-post, f.eks. invitasjoner og varsler.',
      'Google AdSense kan vise annonser i appen. Når dette er aktivert, kan Google sette annonsecookies og behandle enkelte data om enhet og bruk for å vise relevante annonser, i tråd med samtykke du gir.',
    ],
  },
  {
    Icon: Cookie,
    title: 'Informasjonskapsler (cookies)',
    color: 'var(--amber)',
    paragraphs: [
      'Nødvendige cookies brukes for innlogging og sesjon, og krever ikke samtykke.',
      'Selve annonsebiblioteket fra Google lastes teknisk med appen (nødvendig for at Google skal kunne verifisere og godkjenne nettstedet vårt), men setter i seg selv ingen sporingscookies. Faktiske annonser — og dermed ekte annonsecookies — vises kun etter at du har gitt samtykke via samtykkebanneret som vises nederst i appen ved første besøk.',
      'Du kan når som helst endre samtykket ditt for annonsecookies under Profil → Sikkerhet → «Annonsesamtykke» → «Endre». Trekker du tilbake samtykket, stoppes annonsevisning fra neste besøk.',
    ],
  },
  {
    Icon: ShieldCheck,
    title: 'Dine rettigheter',
    color: 'var(--red)',
    paragraphs: [
      'Retting: Navn, avatar, fylke og kommune kan du endre selv når som helst under Profil → Rediger. Ting og inventar redigerer du direkte der de vises i appen.',
      'Innsyn: Du ser allerede dine egne opplysninger direkte i appen — i profilen din, og i ting/inventar du selv har registrert. Ønsker du en fullstendig oversikt over alt vi har lagret om deg, kan du kontakte post@itiliti.no, så sender vi deg dette.',
      'Sletting: Du kan slette kontoen din selv under Profil → Rediger → Slett konto. Dette sletter permanent profilen din (inkludert fylke/kommune), alle dine ting og inventar, lånhistorikk, kommentarer, varsler og gruppemedlemskap, og fjerner selve brukerkontoen din. Handlingen kan ikke angres.',
      'Du kan klage til Datatilsynet dersom du mener personopplysningene dine behandles i strid med regelverket.',
    ],
  },
];

export const PersonvernPage: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-solid)', zIndex: 100, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: 'calc(var(--safe-top) + 14px) 16px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px 2px 0', color: 'var(--text)', display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={24} strokeWidth={2} />
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Personvern</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Personvernerklæring for Toolshare.no</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 32px' }}>
        <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 'var(--radius)', padding: '16px 14px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--green-dark)', marginBottom: 4 }}>Behandlingsansvarlig</div>
          <div style={{ fontSize: 13, color: 'var(--green-dark)', lineHeight: 1.6, opacity: 0.85 }}>
            Toolshare.no, kontakt: post@itiliti.no. Sist oppdatert: juli 2026.
          </div>
        </div>

        {SECTIONS.map(section => (
          <div key={section.title} style={{ marginBottom: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-sm)', background: section.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <section.Icon size={20} color={section.color} strokeWidth={1.8} />
              </div>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{section.title}</span>
            </div>
            <div>
              {section.paragraphs.map((p, i) => (
                <div key={i} style={{ padding: '13px 16px', borderBottom: i < section.paragraphs.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', gap: 12 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: section.color, marginTop: 7, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{p}</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', marginTop: 10 }}>
          <Mail size={18} color="var(--text2)" strokeWidth={1.8} />
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>Spørsmål om personvern? Kontakt <strong style={{ color: 'var(--text)' }}>post@itiliti.no</strong></div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--text3)' }}>
          Toolshare.no
        </div>
      </div>
    </div>
  );
};
